import BomArrow from "@assets/images/bom-arrow.svg";
import { Button, Grid } from "@material-ui/core";
import DatePickerApplyAll from "@saleor/components/DatePickerApplyAll";
import DecimalFormat from "@saleor/components/DecimalFormat";
import { FIELD_TYPE } from "@saleor/components/FormField/constants";
import FormField from "@saleor/components/FormField/FormField";
import MyTypography from "@saleor/components/MyTypography";
import useDebounce from "@saleor/hooks/useDebounce";
import moment from "moment-timezone";
import React from "react";
import InlineSVG from "react-inlinesvg";
import { useIntl } from "react-intl";

import useStyles from "./styles";

const nonEmptyFields = new Set(["materialCode", "itemNo"]);
const emptyMaterialFeilds = new Set([
  "requestDate",
  "originalDate",
  "confirmDate",
  "plant"
]);
const isBomMatEmpty = (bom, cell) => bom && emptyMaterialFeilds.has(cell);

const emptyMiddleware = (formatter, row: NotRefCPOrderLine, col, index) => {
  if (
    (!row?.materialCode && !nonEmptyFields.has(col.id)) ||
    (row?.materialCode && isBomMatEmpty(row?.parentItemNo, col?.id))
  ) {
    if (col.child) {
      return null;
    }
    return (
      <MyTypography variant="body3" align={col?.align || "center"}>
        -
      </MyTypography>
    );
  } else {
    return formatter?.(row, col, index);
  }
};

const quantityFormatter = (row: NotRefOrderLine, col) => (
  <DecimalFormat
    value={+row?.[col?.id]}
    isGrayDecimal={false}
    countDecimal={row?.salesUnit === "KG" || row?.salesUnit === "TON" ? 3 : 0}
  />
);

const commonTextFormatter = (row: NotRefCPOrderLine, cell) => (
  <MyTypography variant={"body3"}>{row[cell.id]}</MyTypography>
);

function useFormatters(formik, sortDirection) {
  const intl = useIntl();
  const classes = useStyles();

  const handleApplyAllRequestDate = value => {
    formik.setValues(prev => ({
      lines: prev.lines.map(item => ({ ...item, requestDate: value }))
    }));
  };

  const handleChange = useDebounce(formik.handleChange, 100);

  const CommonDateField = ({
    name,
    value,
    handleChange,
    minDate = undefined,
    disableFutureDate = false
  }) => (
    <Grid container style={{ height: 32 }}>
      <FormField
        name={name}
        type={FIELD_TYPE.DATEPICKER}
        value={value}
        isUpdateDate={true}
        handleChange={handleChange}
        md={12}
        sm={12}
        disableManualDateInput={true}
        minDate={minDate}
        disableFutureDate={disableFutureDate}
      />
    </Grid>
  );
  const confirmDateFormatter = (row: NotRefCPOrderLine, cell) => (
    <MyTypography variant={"body3"} className={classes.confirmDate}>
      {row[cell.id]}
    </MyTypography>
  );

  const materialCodeFormatter = (row: NotRefCPOrderLine, col: TableColumn) => (
    <div>
      {row.parentItemNo ? (
        <div className={classes.rowFlex}>
          <Button variant={"text"} className={classes.btnExternal}>
            <InlineSVG
              className={sortDirection === "asc" ? "" : classes?.flip}
              src={BomArrow}
              width={24}
              height={24}
            />
          </Button>
          <MyTypography variant={"body3"}>{row[col.id]}</MyTypography>
        </div>
      ) : (
        <div className={classes.rowFlex}>
          <MyTypography variant={"body3"}>
            {row[col.id] || <span style={{ color: "#999" }}>ค้นหาสินค้า</span>}
          </MyTypography>
        </div>
      )}
    </div>
  );

  const ItemNoFormatter = (row: NotRefCPOrderLine, cell) => (
    <MyTypography
      variant={"body3"}
      className={row?.parentItemNo ? classes.itemNoChild : classes.itemNoParent}
      align="center"
    >
      {row[cell.id]}
    </MyTypography>
  );

  const requestDateFormatter = (row, col) => (
    <CommonDateField
      name={`lines[${row?.indexNumber}].${col.id}`}
      value={row[col.id]}
      handleChange={handleChange}
      minDate={moment()}
    />
  );

  const requestDateApplyAllHeaderFormatter = () => (
    <MyTypography variant={"subtitle3"} style={{ position: "relative" }}>
      {intl.formatMessage({ defaultMessage: "Request Date" })}
      <span className={classes.required}>* </span>
      <DatePickerApplyAll
        onApplyAll={handleApplyAllRequestDate}
        minDate={moment()}
      />
    </MyTypography>
  );

  return {
    requestDateFormatter,
    ItemNoFormatter,
    commonTextFormatter,
    materialCodeFormatter,
    emptyMiddleware,
    quantityFormatter,
    requestDateApplyAllHeaderFormatter,
    confirmDateFormatter
  };
}

export default useFormatters;
